import React from 'react';
import { useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  List,
  Kanban,
  GanttChart,
  Calendar,
  MessageSquare,
  Folder,
  
  Vote,
  FileText,
  LucideIcon
} from 'lucide-react';

export type TabId = 'overview' | 'tasks' | 'list' | 'board' | 'timeline' | 'dashboard' | 'messages' | 'votes' | 'planning-poker' | 'notes' | 'collaboration' | 'taches-du-projet' | 'meetings' | 'equipe';

export interface TabConfig {
  id: TabId;
  label: string;
  icon: LucideIcon;
  onClick?: () => void;
  disabled?: boolean;
  active?: boolean; // Override active state if provided
}

interface ProjectNavigationTabsProps {
  tabs: TabConfig[];
  activeTab?: TabId;
  onTabChange?: (tabId: TabId) => void;
  className?: string;
}

const ProjectNavigationTabs: React.FC<ProjectNavigationTabsProps> = ({
  tabs,
  activeTab,
  onTabChange,
  className = ''
}) => {
  const location = useLocation();

  // Determine if a tab is active
  const isTabActive = (tab: TabConfig): boolean => {
    // If active prop is explicitly provided, use it
    if (tab.active !== undefined) {
      return tab.active;
    }
    
    // Check if activeTab matches
    if (activeTab && tab.id === activeTab) {
      return true;
    }
    
    // Special handling for timeline - check if path includes /timeline
    if (tab.id === 'timeline' && location.pathname.includes('/timeline')) {
      return true;
    }
    
    // Special handling for board - check if path includes /tasks
    if (tab.id === 'board' && location.pathname.includes('/tasks')) {
      return true;
    }
    
    return false;
  };

  const handleTabClick = (tab: TabConfig) => {
    if (tab.disabled) return;
    
    if (tab.onClick) {
      tab.onClick();
    } else if (onTabChange) {
      onTabChange(tab.id);
    }
  };

  return (
    <div className={`border-b border-gray-200 ${className}`}>
      <nav className="flex -mb-px w-full">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = isTabActive(tab);
          
          return (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab)}
              disabled={tab.disabled}
              className={`flex items-center justify-center gap-1.5 px-2 py-2 text-xs font-medium border-b-2 transition-all whitespace-nowrap flex-1 min-w-0 group ${
                isActive
                  ? 'border-blue-500 text-blue-600 bg-blue-50/50'
                  : tab.disabled
                  ? 'border-transparent text-gray-300 cursor-not-allowed'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50/50'
              }`}
            >
              <Icon className={`h-3.5 w-3.5 flex-shrink-0 ${isActive ? 'text-blue-600' : 'text-gray-500 group-hover:text-gray-700'}`} />
              <span className="truncate">{tab.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
};

export default ProjectNavigationTabs;

